import { Badge } from 'corso-admin-ui';
import {
  crewClaimResolutionMethodEnumName,
  CrewIncentiveTypeEnum,
  isNonNullable,
  isTruthy,
  maxPolicyEligibilityDays,
  Pegasus,
  shippingClaimResolutionMethodName,
  UploadContentTypeLabel,
  uploadContentTypesFileNoun,
} from 'corso-types';
import { formatter } from 'corso-web-lib';
import { Fragment, ReactNode } from 'react';
import { match } from 'ts-pattern';
import Address from '~/components/Address';
import DescriptionList, { Description } from '~/components/DescriptionList';
import { AutomationExpressionDisplay } from '~/features/settings/automations/AutomationFormPage';
import { useMerchantPaidProtectUnits } from '~/features/settings/automations/hooks/useMerchantPaidProtectUnits';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';
import { useProductGroups } from '~/features/settings/hooks/useProductGroups';
import { useProtectionRates } from '~/features/settings/hooks/useProtectionRates';
import ManageShippingRates from '~/features/settings/shippingPlus/ManageShippingRates';
import { useCurrency } from '~/hooks/useCurrency';
import { useReturnLocations } from '~/hooks/useReturnLocations';
import { Automation, inventoryBehaviorLabels } from '~/types';
import { discountTypeText } from '~/utils/enumNameMaps';
import useFulfillmentLocations from '../hooks/useFulfillmentLocations';
import EventCreateGorgiasTicketDisplay from './EventCreateGorgiasTicketDisplay';
import EventCustomizeReturnShipmentDisplay from './EventCustomizeReturnShipmentDisplay';
import DisplayEventOfferAdvancedExchange from './EventOfferAdvancedExchange';
import DisplayEventOrderEditing from './EventOrderEditingDisplay';
import EventRegistrationDiscountDisplay from './EventRegistrationDiscountDisplay';
import EventRestockInventoryDisplay from './EventRestockInventoryDisplay';
import EventShopNowDisplay from './EventShopNowDisplay';

const eligibilityHelper = (value?: number | null) => {
  if (value === 0) return 'Not Offered';

  if (!value) return 'Using Store Defaults';
  if (value === maxPolicyEligibilityDays) return 'Lifetime';
  return `Within ${value} Days`;
};

const automationEventDisplay = {
  [Pegasus.EventType.shopNow]: (params) => (
    <EventShopNowDisplay params={params} />
  ),
  [Pegasus.EventType.merchantPaidShippingProtection]: ({
    shipProtectUnitId,
  }) => {
    const protectSetUnits = useMerchantPaidProtectUnits();
    const chargeAmount = protectSetUnits.data?.find(
      (v) => v.id === shipProtectUnitId,
    )?.chargeAmount;

    const list: Description[] = [];
    if (chargeAmount) {
      list.push({
        term: 'Merchant Paid Shipping Protection',
        details: `${formatter.currency(chargeAmount, 'USD')} per eligible order`,
      });
    } else {
      list.push({
        term: 'Merchant Paid Shipping Protection',
        details: 'Not Offered',
      });
    }

    return <DescriptionList layoutType="list" descriptions={list} />;
  },
  [Pegasus.EventType.modifyResolutionWindow]: ({
    kind,
    exchange,
    giftCard,
    refund,
    warrantyReview,
    ineligibleLineItemFootnote,
    isIneligibleWarrantyClaimAllowed,
    ineligibleWarrantyClaimTitle,
  }) => {
    const isEligibilityDays = kind === 'eligibilityDays';

    const list: Description[] = [];

    if (isEligibilityDays) {
      list.push({ term: 'Refund', details: eligibilityHelper(refund) });
      list.push({ term: 'Gift Card', details: eligibilityHelper(giftCard) });
      list.push({ term: 'Exchange', details: eligibilityHelper(exchange) });
      list.push({
        term: 'Warranty Review',
        details: eligibilityHelper(warrantyReview),
      });
    } else {
      if (refund)
        list.push({
          term: 'Refund',
          details: `Until ${formatter.date(refund)}`,
        });
      if (giftCard)
        list.push({
          term: 'Gift Card',
          details: `Until ${formatter.date(giftCard)}`,
        });
      if (exchange)
        list.push({
          term: 'Exchange',
          details: `Until ${formatter.date(exchange)}`,
        });
      if (warrantyReview) {
        list.push({
          term: 'Warranty Review',
          details: `Until ${formatter.date(warrantyReview)}`,
        });
      }
    }

    if (ineligibleLineItemFootnote) {
      list.push({
        term: 'Ineligible Line Item Footnote',
        details: ineligibleLineItemFootnote,
      });
    }

    if (isIneligibleWarrantyClaimAllowed) {
      list.push({
        term: 'Warranty Claims Outside The Window',
        details: 'Allowed',
      });

      if (ineligibleWarrantyClaimTitle) {
        list.push({
          term: 'Outside Window Title',
          details: ineligibleWarrantyClaimTitle,
        });
      }
    }

    return <DescriptionList layoutType="grid" descriptions={list} />;
  },
  [Pegasus.EventType.autoFinalizeClaim]: () => (
    <p className="text-sm font-semibold text-neutral-500">
      Auto-Finalize Claim{' '}
    </p>
  ),
  [Pegasus.EventType.collectCustomFields]: ({
    customFieldIds,
    isMediaUploadRequired,
    mediaUploadInstructions,
    minimumMediaUploadAmount,
    allowedContentTypes,
    showDefaultCommentField,
    isDefaultCommentFieldRequired,
  }) => {
    const { data: customFields } = useCustomFields();

    const inUseCustomFields = customFields?.filter((field) =>
      customFieldIds.includes(field.id),
    );

    const descriptions = [
      ...(inUseCustomFields?.length
        ? [
            {
              term: 'Collect Custom Fields',
              details: inUseCustomFields
                .map((field) => field.displayName)
                .join(', '),
            },
          ]
        : []),
      {
        term: 'Show Default Comment Field',
        details: `${showDefaultCommentField ? 'Yes' : 'No'}`,
      },
      {
        term: 'Require Default Comment Field',
        details: `${isDefaultCommentFieldRequired ? 'Yes' : 'No'}`,
      },
      isMediaUploadRequired && {
        term: 'Require Media Upload',
        details: `${minimumMediaUploadAmount} ${uploadContentTypesFileNoun(allowedContentTypes)}${minimumMediaUploadAmount === 1 ? '' : 's'}`,
      },
      {
        term: 'Accepted Media',
        details: allowedContentTypes
          .map((contentType) => UploadContentTypeLabel[contentType])
          .join(', '),
      },
      mediaUploadInstructions && {
        term: 'Media Upload Instructions',
        details: mediaUploadInstructions,
      },
    ].filter(isTruthy);

    return <DescriptionList layoutType="list" descriptions={descriptions} />;
  },
  [Pegasus.EventType.offerInstantExchange]: ({
    timeToReturnBeforeCharging,
    isInstantExchangeRequired,
    instantExchangeDescriptionText,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Instant Exchange Window',
          details: `Customers have ${timeToReturnBeforeCharging} days to return the original item before being charged.`,
        },
        {
          term: 'Exchange Timing',
          details: 'The exchange order is processed at the time of the return.',
        },
        {
          term: 'Require Instant Exchange When Offered',
          details: isInstantExchangeRequired ? 'Yes' : 'No',
        },
        instantExchangeDescriptionText
          ? {
              term: 'Detail Text',
              details: instantExchangeDescriptionText,
            }
          : null,
      ].filter(isTruthy)}
    />
  ),
  [Pegasus.EventType.askAPolicyEnforcingQuestion]: ({
    question,
    onYesMessage,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Ask Enforcing Question',
          details: question,
        },
        ...(onYesMessage
          ? [
              {
                term: 'If Yes',
                details: onYesMessage,
              },
            ]
          : []),
      ]}
    />
  ),
  [Pegasus.EventType.allowClaimTypeSelection]: ({
    claimTypeSelectionDetailText,
    returnSelectionButtonTitle,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Select Return Or Warranty',
          details: claimTypeSelectionDetailText,
        },
        {
          term: 'Return Selection Button Title',
          details: returnSelectionButtonTitle ?? 'Return',
        },
      ]}
    />
  ),
  [Pegasus.EventType.omitResolutionMethods]: ({ omittedResolutionMethods }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Omitted Resolution Methods',
          details: omittedResolutionMethods.length
            ? omittedResolutionMethods
                .map((method) => crewClaimResolutionMethodEnumName[method])
                .join(', ')
            : 'None',
        },
      ]}
    />
  ),
  [Pegasus.EventType.applyClaimTags]: ({ claimTags }) => (
    <DescriptionList
      layoutType="grid"
      descriptions={[
        {
          term: 'Apply Tags To',
          details: 'Claim',
        },
        {
          term: 'Tags',
          details: claimTags.map((v) => v.name).join(', '),
        },
      ]}
    />
  ),

  [Pegasus.EventType.applyOrderTags]: ({
    orderTags,
    orderTagAction,
    tagWithReason,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Apply Tags To',
          details: orderTagAction,
        },
        {
          term: 'Tags',
          details: orderTags.length
            ? orderTags.map((v) => v.name).join(', ')
            : 'None',
        },
        {
          term: 'Tag With Reason',
          details: `${tagWithReason ? 'Yes' : 'No'}`,
        },
      ]}
    />
  ),
  [Pegasus.EventType.customizeClaimFinalizationSettings]: ({
    failOnOutOfStockScenarios,
    inventoryBehavior,
    allowFulfillmentLocationIds,
  }) => {
    const locations = useFulfillmentLocations();
    const descriptions: Description[] = [];

    descriptions.push({
      term: 'Fail on Out of Stock',
      details: failOnOutOfStockScenarios ? 'Yes' : 'No',
    });

    if (inventoryBehavior) {
      descriptions.push({
        term: 'Inventory Behavior',
        details: inventoryBehaviorLabels[inventoryBehavior],
      });
    }

    if (allowFulfillmentLocationIds?.length) {
      descriptions.push({
        term: 'Allowed Fulfillment Locations',
        details: allowFulfillmentLocationIds
          .map(
            (id) =>
              locations.data?.find((location) => location.id === id)?.name,
          )
          .join(', '),
      });
    }

    return <DescriptionList layoutType="grid" descriptions={descriptions} />;
  },
  [Pegasus.EventType.modifyIncentives]: ({ giftCardIncentive }) => {
    const { currencyCode } = useCurrency();

    let displayValue = 'None';

    if (!giftCardIncentive.shouldModify) {
      displayValue = 'Using Store Defaults';
    } else if (giftCardIncentive.type === CrewIncentiveTypeEnum.fixed) {
      displayValue = formatter.currency(giftCardIncentive.amount, currencyCode);
    } else if (giftCardIncentive.type === CrewIncentiveTypeEnum.percent) {
      displayValue = formatter.percent(giftCardIncentive.amount);
    }

    return (
      <DescriptionList
        layoutType="grid"
        descriptions={[
          {
            term: 'Offer Gift Card Incentive Amount',
            details: displayValue,
          },
        ]}
      />
    );
  },
  [Pegasus.EventType.applyFee]: ({ feeAmount, feeDisplayName }) => {
    const { currencyCode } = useCurrency();

    if (feeAmount <= 0) {
      return (
        <p className="text-sm font-semibold text-neutral-500">Waive Fee</p>
      );
    }

    return (
      <>
        <p className="text-sm font-semibold text-neutral-500">Collect Fee</p>
        <DescriptionList
          layoutType="grid"
          descriptions={[
            {
              term: 'Amount',
              details: formatter.currency(feeAmount, currencyCode),
            },
            {
              term: 'Display Name',
              details: feeDisplayName,
            },
          ]}
        />
      </>
    );
  },
  [Pegasus.EventType.applyLineItemFee]: ({
    feeAmount,
    feeDisplayName,
    applyPerItem,
  }) => {
    const { currencyCode } = useCurrency();

    if (feeAmount <= 0) {
      return (
        <p className="text-sm font-semibold text-neutral-500">Waive Fee</p>
      );
    }

    return (
      <>
        <p className="text-sm font-semibold text-neutral-500">Collect Fee</p>
        <DescriptionList
          layoutType="grid"
          descriptions={[
            {
              term: 'Amount',
              details: formatter.currency(feeAmount, currencyCode),
            },
            {
              term: 'Display Name',
              details: feeDisplayName,
            },
            {
              term: 'Charge Per Item',
              details: applyPerItem ? 'Yes' : 'No',
            },
          ]}
        />
      </>
    );
  },
  [Pegasus.EventType.chargeForReturnLabel]: ({ applyLabelMarkup }) => {
    const { currencyCode } = useCurrency();

    if (!applyLabelMarkup.shouldModify)
      return (
        <p className="text-sm font-semibold text-neutral-500">
          Charge For Return Label
        </p>
      );

    const markup =
      applyLabelMarkup.type === 'Fixed'
        ? formatter.currency(applyLabelMarkup.amount, currencyCode)
        : formatter.percent(applyLabelMarkup.amount);

    return (
      <DescriptionList
        layoutType="grid"
        descriptions={[
          {
            term: 'Charge For Return Label',
            details: 'Yes',
          },
          {
            term: 'Return Label Markup Amount',
            details: markup,
          },
        ]}
      />
    );
  },

  [Pegasus.EventType.chargeForExchangeOrderShipping]: ({
    applyExchangeOrderShippingMarkup,
  }) => {
    const { currencyCode } = useCurrency();

    if (!applyExchangeOrderShippingMarkup.shouldModify)
      return (
        <p className="text-sm font-semibold text-neutral-500">
          Charge For Exchange Order Shipping
        </p>
      );

    const markup =
      applyExchangeOrderShippingMarkup.type === 'Fixed'
        ? formatter.currency(
            applyExchangeOrderShippingMarkup.amount,
            currencyCode,
          )
        : formatter.percent(applyExchangeOrderShippingMarkup.amount);

    return (
      <DescriptionList
        layoutType="grid"
        descriptions={[
          {
            term: 'Charge For Exchange Order Shipping',
            details: 'Yes',
          },
          {
            term: 'Markup Amount',
            details: markup,
          },
        ]}
      />
    );
  },
  [Pegasus.EventType.productRegistrationSelection]: ({
    productRegistrationProductGroupId,
  }) => {
    const { data: productGroups } = useProductGroups();
    const productGroup = productGroups.find(
      ({ id }) => id === productRegistrationProductGroupId,
    );

    if (!productGroup)
      return (
        <p className="text-sm text-neutral-700">No Product Group Configured</p>
      );

    return (
      <DescriptionList
        layoutType="list"
        descriptions={[
          {
            term: 'Product Registration Group',
            details: productGroup.name,
          },
        ]}
      />
    );
  },
  [Pegasus.EventType.allowCustomerToKeepItem]: () => (
    <div className="flex flex-col items-center gap-2">
      <p className="text-sm font-semibold text-neutral-500">Manual Review</p>
      <p className="text-xs text-neutral-500">
        Customers will not be provided a return label or packing slip
        automatically.
      </p>
    </div>
  ),
  [Pegasus.EventType.quoteOrderShipment]: ({
    storeShipRateIds,
    storeRuleId,
  }) => {
    const { data: protectionRates } = useProtectionRates();

    const inUseRuleRates = protectionRates.filter((rate) =>
      storeShipRateIds.includes(rate.id),
    );

    if (!inUseRuleRates.length)
      return <p className="text-sm text-neutral-700">No Rates Configured</p>;

    return (
      <div>
        <ManageShippingRates
          rates={inUseRuleRates}
          storeRuleId={storeRuleId}
          editable
        />
      </div>
    );
  },
  [Pegasus.EventType.customizeClaimLineItemInspection]: ({
    inspectionInstructions,
    shouldCollectInspectionGrade,
    showInspectionComment,
    showInspectionMediaUpload,
    inspectionCustomFieldIds,
  }) => {
    const { data: customFields } = useCustomFields();

    const descriptions: Description[] = [];

    if (inspectionInstructions) {
      descriptions.push({
        term: 'Inspection Instructions',
        details: inspectionInstructions,
      });
    }

    if (shouldCollectInspectionGrade) {
      descriptions.push({
        term: 'Collect Inspection Grade',
        details: 'Yes',
      });
    }

    if (showInspectionComment) {
      descriptions.push({
        term: 'Show Inspection Comment',
        details: 'Yes',
      });
    }

    if (inspectionCustomFieldIds.length) {
      const customFieldNames = inspectionCustomFieldIds
        .map(
          (id) => customFields?.find((field) => field.id === id)?.displayName,
        )
        .filter(Boolean)
        .join(', ');

      if (customFieldNames) {
        descriptions.push({
          term: 'Inspection Custom Fields',
          details: customFieldNames,
        });
      }
    }

    if (showInspectionMediaUpload) {
      descriptions.push({
        term: 'Show Inspection Media Upload',
        details: 'Yes',
      });
    }

    return <DescriptionList layoutType="grid" descriptions={descriptions} />;
  },
  [Pegasus.EventType.returnShippingConfig]: (params) => {
    const { data: returnLocations } = useReturnLocations();

    return match(params)
      .with({ returnHandling: Pegasus.ReturnHandling.globalE }, () => (
        <div className="flex flex-col gap-1 text-sm text-neutral-700">
          <Badge>Managed By Global-e</Badge>
          <p className="text-xs text-neutral-500">
            Global-e determines where items are returned to and issues the
            label.
          </p>
        </div>
      ))
      .with(
        { returnHandling: Pegasus.ReturnHandling.returnLocation },
        ({ returnMethods, returnLocationId }) => {
          const returnTo = returnLocations?.find(
            ({ id }) => id === returnLocationId,
          );

          return (
            <div className="flex flex-col gap-4 text-sm text-neutral-700">
              <div>
                {returnTo && (
                  <div className="mt-1 flex flex-col gap-1">
                    <p className="mb-1 font-semibold text-neutral-500">
                      Return Items To:
                    </p>
                    <Address address={returnTo.address} name={returnTo.name} />
                  </div>
                )}
              </div>

              <div>
                <p className="mb-1 text-sm font-semibold text-neutral-500">
                  Offered Return Methods:
                </p>
                <div className="flex flex-wrap gap-1 text-xs text-neutral-500">
                  {returnMethods.map((method) => (
                    <Badge key={method}>{method}</Badge>
                  ))}
                </div>
              </div>
            </div>
          );
        },
      )
      .exhaustive();
  },
  [Pegasus.EventType.quoteOrderShipmentModify]: ({
    checkoutRateModifications,
  }) => {
    const { currencyCode } = useCurrency();

    const { data: protectionRates } = useProtectionRates();

    return (
      <div className="mt-2 flex flex-col gap-2">
        {checkoutRateModifications.map((modification, index) => (
          // TODO find a better alternative to `index` as a key, as there isn't necessarily a unique identifier that wouldn't depend on it
          // oxlint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            {match(modification)
              .with({ kind: 'APPLY_FIXED_FEE' }, ({ amount, applyPerItem }) => (
                <div>
                  <p className="font-medium">Apply Fixed Fee</p>
                  <div className="m-2">
                    <p>
                      {' '}
                      {formatter.currency(amount, currencyCode)}{' '}
                      {applyPerItem && 'per item'}
                    </p>
                  </div>
                </div>
              ))
              .with({ kind: 'APPLY_PERCENTAGE_FEE' }, ({ percentage }) => (
                <div>
                  <p className="font-medium">Apply Percentage Fee</p>
                  <div className="m-2">
                    <p> {percentage * 100}% of Rate Price </p>
                  </div>
                </div>
              ))
              .with(
                { kind: 'ADD_OR_REMOVE_RATES' },
                ({ addOrRemoveStoreShipRateIds, action }) => {
                  const inUseRuleRates = protectionRates.filter((rate) =>
                    addOrRemoveStoreShipRateIds.includes(rate.id),
                  );

                  const description = {
                    ADD: 'Add',
                    REMOVE: 'Remove',
                    REPLACE: 'Replace',
                  } satisfies Record<typeof action, string>;

                  return (
                    <div>
                      <p className="font-medium">{description[action]} Rates</p>
                      <ManageShippingRates
                        rates={inUseRuleRates}
                        editable={false}
                      />
                    </div>
                  );
                },
              )
              .with(
                { kind: 'APPLY_SHIPPING_DISCOUNT' },
                ({
                  discountAmount,
                  discountType,
                  isCheckboxApplicationEnabled,
                  discountMessage,
                }) => (
                  <div>
                    <p className="font-medium">Apply Shipping Discount</p>

                    <div className="m-2 flex flex-col gap-1.5">
                      <p>
                        {discountTypeText[discountType]}{' '}
                        {isCheckboxApplicationEnabled &&
                          'at (Checkbox Application)'}
                      </p>
                      {discountType !== 'removePlusPricing' && (
                        <p>
                          Amount:{' '}
                          {discountType === 'fixedAmount'
                            ? formatter.currency(discountAmount, currencyCode)
                            : `${discountAmount * 100}%`}
                        </p>
                      )}
                      Message: {discountMessage ?? 'No message provided'}
                    </div>
                  </div>
                ),
              )
              .exhaustive()}
          </Fragment>
        ))}
      </div>
    );
  },

  [Pegasus.EventType.quoteOrderShipmentParcel]: ({ parcel }) => (
    <div>
      <p className="font-medium">Custom Parcel</p>
      <div className="m-2">
        <p> {parcel.name}</p>
        <p>
          Length: {parcel.length} Width: {parcel.width}
          {!!parcel.height && <p>Height: {parcel.height}</p>}
        </p>
      </div>
    </div>
  ),
  [Pegasus.EventType.offerAdvancedExchange]: (params) => (
    <DisplayEventOfferAdvancedExchange params={params} />
  ),
  [Pegasus.EventType.orderEditing]: (params) => (
    <DisplayEventOrderEditing params={params} />
  ),

  [Pegasus.EventType.quoteOrderShipmentDeliveryEstimate]: ({
    storeRuleId,
    deliveryEstimateConfigs,
  }) => {
    const { data: protectionRates } = useProtectionRates();

    return (
      <div className="mt-2 flex flex-col gap-4">
        {deliveryEstimateConfigs.map((config, index) => {
          const {
            estimatedDeliveryRangeStartDays,
            estimatedDeliveryDisplayLocation,
            estimatedTargetStoreShipRateIds,
            estimatedDeliveryRangeEndDays,
            estimatedDeliveryBlackoutDates,
            strategy,
          } = config;

          const inUseRuleRates = protectionRates.filter((rate) =>
            estimatedTargetStoreShipRateIds.includes(rate.id),
          );

          return (
            <div
              className="rounded-lg border p-4 shadow-xs"
              // TODO evaluate a better alternative to the `index` as a key
              // oxlint-disable-next-line react/no-array-index-key
              key={`${storeRuleId}-${index}`}
            >
              <p className="font-medium">Provide Estimated Delivery Date</p>
              <p className="m-2">
                {strategy === 'CARRIER_PROVIDED'
                  ? 'Carrier Provided'
                  : 'Predefined Estimate'}
              </p>
              {estimatedDeliveryRangeStartDays && (
                <p className="m-2">
                  Estimated Delivery Start: {estimatedDeliveryRangeStartDays}{' '}
                  business days
                </p>
              )}
              {estimatedDeliveryRangeEndDays && (
                <p className="m-2">
                  Estimated Delivery End: {estimatedDeliveryRangeEndDays}{' '}
                  business days
                </p>
              )}

              <p className="m-2">
                Display Location:{' '}
                {estimatedDeliveryDisplayLocation === 'EXTENSION'
                  ? 'Shipping Line Extension (Requires Shopify Plus)'
                  : 'Replace Rate Description'}
              </p>

              {estimatedDeliveryBlackoutDates &&
                estimatedDeliveryBlackoutDates.length > 0 && (
                  <div className="m-2">
                    <DescriptionList
                      layoutType="list"
                      descriptions={[
                        {
                          term: 'Blackout Dates',
                          details: (
                            <div className="flex flex-wrap gap-1">
                              {estimatedDeliveryBlackoutDates
                                .slice()
                                .sort(
                                  (a, b) =>
                                    new Date(a).getTime() -
                                    new Date(b).getTime(),
                                )
                                .map((date) => (
                                  <Badge key={date}>
                                    {formatter.date(date)}
                                  </Badge>
                                ))}
                            </div>
                          ),
                        },
                      ]}
                    />
                  </div>
                )}

              <p className="font-medium">Rates To Provide Estimates For:</p>
              {estimatedTargetStoreShipRateIds.length ? (
                <ManageShippingRates rates={inUseRuleRates} editable={false} />
              ) : (
                <p className="mt-1 font-medium">All Rates</p>
              )}
            </div>
          );
        })}
      </div>
    );
  },
  [Pegasus.EventType.restockInventory]: (params) => (
    <EventRestockInventoryDisplay params={params} />
  ),
  [Pegasus.EventType.registrationDiscountIncentive]: (params) => (
    <EventRegistrationDiscountDisplay params={params} />
  ),
  [Pegasus.EventType.customizeReturnShipment]: (params) => (
    <EventCustomizeReturnShipmentDisplay params={params} />
  ),
  [Pegasus.EventType.createGorgiasTicket]: (params) => (
    <EventCreateGorgiasTicketDisplay params={params} />
  ),
  [Pegasus.EventType.rightToWithdraw]: ({
    rightToWithdrawDescription,
    rightToWithdrawTitle,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        { term: 'Title', details: rightToWithdrawTitle },
        { term: 'Description', details: rightToWithdrawDescription },
      ]}
    />
  ),
  [Pegasus.EventType.setOrderShippingTitle]: ({ shippingTitle }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[{ term: 'Shipping Title', details: shippingTitle }]}
    />
  ),
  [Pegasus.EventType.shipClaimAllowedResolutions]: ({
    shippingClaimResolutionMethods,
    isGiftCardEnabled,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Allowed Resolution Methods',
          details: shippingClaimResolutionMethods
            .map((method) => shippingClaimResolutionMethodName[method])
            .join(', '),
        },
        {
          term: 'Allow Gift Card',
          details: isGiftCardEnabled ? 'Yes' : 'No',
        },
      ]}
    />
  ),
  [Pegasus.EventType.shipClaimReorderLocations]: ({
    reorderLocationIds,
    minInventoryAvailable,
  }) => {
    const locations = useFulfillmentLocations();

    return (
      <DescriptionList
        layoutType="list"
        descriptions={[
          {
            term: 'Allowed Reorder Locations',
            details: reorderLocationIds
              .map(
                (id) =>
                  locations.data?.find((location) => location.id === id)?.name,
              )
              .filter(isTruthy)
              .join(', '),
          },
          isNonNullable(minInventoryAvailable) && {
            term: 'Minimum Inventory Available',
            details: minInventoryAvailable,
          },
        ].filter(isTruthy)}
      />
    );
  },
  [Pegasus.EventType.shipClaimReorderShipping]: (params) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        params.shippingTitleKind === 'assignment'
          ? { term: 'Assigned Shipping Title', details: params.shippingTitle }
          : {
              term: 'Offered Shipping Titles',
              details: params.shippingTitles.join(', '),
            },
      ]}
    />
  ),
  [Pegasus.EventType.shipApplyOrderTags]: ({ orderTags, orderTagAction }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        {
          term: 'Apply Tags To',
          details:
            orderTagAction === 'Replacement Order' ? 'Reorder' : orderTagAction,
        },
        {
          term: 'Tags',
          details: orderTags.length
            ? orderTags.map((v) => v.name).join(', ')
            : 'None',
        },
      ]}
    />
  ),
  [Pegasus.EventType.shipClaimSlaAlert]: ({
    alert,
    shouldBlockFinalization,
  }) => (
    <DescriptionList
      layoutType="list"
      descriptions={[
        { term: 'Severity', details: alert.variant },
        { term: 'Title', details: alert.title },
        { term: 'Message', details: alert.message },
        shouldBlockFinalization && {
          term: 'Block Finalization',
          details: 'Yes',
        },
      ].filter(isTruthy)}
    />
  ),
} satisfies {
  [K in Pegasus.EventType]: (
    eventParams: Pegasus.EventParams<K> & { storeRuleId: number },
  ) => ReactNode;
};

export function AutomationEventDisplay({
  automation,
}: {
  automation: Automation;
}) {
  return (
    <div className="flex flex-col items-center gap-6 text-neutral-800">
      <AutomationExpressionDisplay rule={automation.rule} />

      {automationEventDisplay[automation.rule.event.type]({
        ...automation.rule.event.params,
        storeRuleId: automation.id,
      } as never)}
    </div>
  );
}
